import React, { useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, message, Space, Table, Input } from "antd";
import type { TableColumnsType, InputRef, TableColumnType } from 'antd';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import "./data_allocate_work_order.css";
import useSWR, { mutate } from 'swr';
import axios from 'axios';
import { useNavigate } from "react-router-dom";

const fetcher = (url: string) => {
    const token = localStorage.getItem('authToken');
    return axios.get(url, {
        headers: {
            Authorization: token ? `Bearer ${token}` : ''
        }
    }).then((res) => res.data);
};

interface TableAllocateWorkOrder {
    key: React.Key;
    id: number;
    work_order_no: string;
    service_advisor: string;
    service_date: string;
    foreman: string;
    foreman_name: string;
    customer_name: string;
    status: string;
    warehouse: string;
    warehouse_name: string;
    overtime?: string | number;
    has_paused?: boolean;
}

const DataAllocateWorkOrder: React.FC = () => {
    const navigate = useNavigate();
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: keyof TableAllocateWorkOrder,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex as string);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: keyof TableAllocateWorkOrder): TableColumnType<TableAllocateWorkOrder> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex as string);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => close()}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase()) : false,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
    });

    const { data, isLoading } = useSWR(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/list`,
        fetcher
    );

    const handleOvertime = async (workOrderNo: string, warehouse: string) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/process-overtime`, {
                work_order_no: workOrderNo,
                warehouse: warehouse
            });
            message.success("Data Overtime berhasil diproses");
            mutate(`${process.env.REACT_APP_BACKEND_URL}/api/work-order-allocation/list`);
        } catch (error) {
            message.error("Gagal memproses overtime");
        }
    };

    const columns: TableColumnsType<TableAllocateWorkOrder> = [
        {
            title: 'Overtime',
            key: 'overtime',
            width: 120,
            render: (_, record) => (record.overtime === "1" || record.overtime === 1) ? (
                <Button type="primary" danger onClick={() => handleOvertime(record.work_order_no, record.warehouse)}>Overtime</Button>
            ) : null,
        },
        {
            title: 'Work Order No',
            dataIndex: 'work_order_no',
            key: 'workOrderNo',
            sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no),
            ...getColumnSearchProps('work_order_no')
        },
        {
            title: 'Service Advisor',
            dataIndex: 'service_advisor',
            key: 'serviceAdvisor',
            sorter: (a, b) => a.service_advisor.localeCompare(b.service_advisor),
            ...getColumnSearchProps('service_advisor')
        },
        {
            title: 'Service Date',
            dataIndex: 'service_date',
            key: 'serviceDate',
            sorter: (a, b) => a.service_date.localeCompare(b.service_date),
            ...getColumnSearchProps('service_date')
        },
        {
            title: 'Foreman',
            dataIndex: 'foreman_name',
            key: 'foreman',
            sorter: (a, b) => a.foreman_name.localeCompare(b.foreman_name),
            ...getColumnSearchProps('foreman_name')
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customerName',
            sorter: (a, b) => a.customer_name.localeCompare(b.customer_name),
            ...getColumnSearchProps('customer_name')
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            sorter: (a, b) => a.status.localeCompare(b.status),
            filters: [
                { text: 'Draft', value: '1' },
                { text: 'On Going', value: '7' },
                { text: 'On Going / Paused', value: '7_P' },
                { text: 'QC Pass', value: '6' },
                { text: 'Waiting for Approved', value: '3' },
                { text: 'Waiting for Approved QC Pass', value: '33' },
                { text: 'Cancel', value: '5' },
            ],
            onFilter: (value, record) => {
                if (value === '7_P') {
                    return record.status === '7' && !!record.has_paused;
                }
                if (value === '7') {
                    return record.status === '7' && !record.has_paused;
                }
                return record.status === value;
            },
            render: (status: string, record: TableAllocateWorkOrder) => {
                switch (status) {
                    case '1': return 'Draft';
                    case '7': return record.has_paused ? 'On Going / Paused' : 'On Going';
                    case '6': return 'QC Pass';
                    case '3': return 'Waiting for Approved';
                    case '33': return 'Waiting for Approved QC Pass';
                    case '5': return 'Cancel';
                    default: return status;
                }
            }
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse_name',
            key: 'warehouse',
            sorter: (a, b) => a.warehouse_name.localeCompare(b.warehouse_name),
            ...getColumnSearchProps('warehouse_name')
        },
    ];

    return (
        <div id="iimk" style={transformStyle}>
            <div className="gjs-grid-row" id="iaa4">
                <div className="gjs-grid-column" id="iij4">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Allocate Work Order</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/work_order_allocation",
                                title: "Work Order Allocation",
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="iefe">
                <div className="gjs-grid-column" id="i0uw">
                    <Table<TableAllocateWorkOrder>
                        columns={columns}
                        dataSource={data?.list || []}
                        loading={isLoading}
                        pagination={{ pageSize: 10 }}
                        bordered
                        size="middle"
                        rowKey="id"
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                navigate("/dashboard/allocate_work_order", { state: { record } });
                            },
                            style: { cursor: 'pointer' }
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataAllocateWorkOrder;